/* eslint-disable no-shadow */
import _get from 'lodash/get';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _zipObject from 'lodash/zipObject';
import _omitBy from 'lodash/omitBy';
import _find from 'lodash/find';
import _size from 'lodash/size';
import _reduce from 'lodash/reduce';
import { STRINGS } from 'config';
import { extractDateFromString } from 'common/dates';
import { getSearchResultDisplayName } from 'components/consultation/answers/search';
import { prefillDemographicData } from './helpers/helpers';

const consultation = STRINGS.CONSULTATION_REDUCER;
const { COMPOUND } = STRINGS.QUESTION_TYPE;

function getCompoundAnswerDefaultValue(type) {
  if (type === 'DATE') {
    return { d: '', m: '', y: '' };
  }
  if (type === 'SINGLE') {
    return {};
  }
  return '';
}

function getCompoundAnswerDefaultAnswerId(type, subQuestion) {
  if (type === 'SINGLE') {
    return null;
  }
  return subQuestion.answers[0].answerId;
}

function primeCompoundAnswer(subQuestions) {
  const answers = _reduce(subQuestions, (result, subQuestion) => {
    if (subQuestion.type === COMPOUND) {
      return subQuestion.questions.reduce((res, ea) => {
        return {
          ...res,
          [ea.questionId]: {
            value: '',
            questionTags: ea.tags,
            answerId: ea.answers[0].answerId,
          },
        };
      }, result);
    }

    return {
      ...result,
      [subQuestion.questionId]: {
        value: getCompoundAnswerDefaultValue(subQuestion.type),
        questionTags: subQuestion.tags,
        answerId: getCompoundAnswerDefaultAnswerId(subQuestion.type, subQuestion),
      },
    };
  }, {});

  return answers;
}

export const initialState = {
  treatmentCategory: '',
  treatmentCategoryDetails: {
    name: '',
    version: '',
  },
  singleSelectedAnswer: {},
  multiSelectedAnswers: [],
  dateAnswer: {
    answerId: '',
    y: '',
    m: '',
    d: '',
  },
  freeTextAnswer: '',
  question: {},
  apiError: null,
  isLoading: false,
  isLoadingLastQuestion: false,
  apiVersion: '',
  sessionExpiryTime: {},
  validationErrors: [],
  submissionDetails: {},
  consultationStartTime: {},
  paused: false,
  isForwardNavigation: true,
  previousAnswers: null,
  practice: {
    practiceId: null,
    odsCode: null,
  },
  encounterId: '',
  error: '',
  isDuplicateSubmission: false,
  isSessionExpiryWarningOpen: false,
  isAttemptingToLeavePage: false,
  isDemoNoticeOpen: true,
  isGAInitialised: false,
  federatedLoginProvider: null,
  postcodeAPIResults: [],
  postcodeValidated: false,
  addressesAPIResults: [],
  compoundAnswers: {},
  bloodPressureAnswer: {
    bloodPressure: {
      0: { systolic: '', diastolic: '', date: '', time: '' },
      1: { systolic: '', diastolic: '', date: '', time: '' },
      2: { systolic: '', diastolic: '', date: '', time: '' },
    },
  },
  bmi: {
    weight: { stone: '', pounds: '', kilograms: '' },
    height: { feet: '', inches: '', centimetres: '' },
    isWeightImperial: true,
    isHeightImperial: true,
  },
  photos: {},
  loginDetails: {},
  publicHealthAlertHtml: '',
  isInternalRedirectBlockResume: false,
  isLoggedIn: false,
  product: '',
  patient: {},
  questionIndex: 0,
  inputsReadOnly: false,
  twoWayMessageId: '',
  isContinueAsGuest: false,
  searchTerm: '',
  searchResults: { results: [], default: {} },
  selectedSearchResult: null,
  syncRequest: false,
  interstitialText: '',
};

const addRemovableFlag = bloodPressureReadings => {
  return _map(bloodPressureReadings, (reading, index) => {
    return index <= 2 ? reading : ({ ...reading, removable: true });
  });
};

const toObject = array => {
  const keys = _map(array, (item, index) => index);
  return _zipObject(keys, array);
};

const splitDateAndTime = bloodPressureReadings => {
  /* Give time field today's date */
  const today = new Date();
  return _map(bloodPressureReadings, reading => {
    const dateFromReading = new Date(reading.date);
    return {
      ...reading,
      time: new Date(today.getFullYear(), today.getMonth(), today.getDate(), dateFromReading.getHours(), dateFromReading.getMinutes(), dateFromReading.getSeconds()),
    };
  });
};

const updateAnswersWithPrevious = ({ question, previousAnswers }, state = {}) => {
  if (!previousAnswers) {
    return null;
  }
  switch (question.type) {
    case STRINGS.QUESTION_TYPE.MULTIPLE:
      return { multiSelectedAnswers: previousAnswers };
    case STRINGS.QUESTION_TYPE.SINGLE:
      return { singleSelectedAnswer: previousAnswers[0] };
    case STRINGS.QUESTION_TYPE.DATE: {
      const dateString = _get(previousAnswers[0], 'valueDate', '');
      const answerId = _get(previousAnswers[0], 'answerId', '');
      return {
        dateAnswer: {
          ...initialState.dateAnswer,
          ...(state.dateAnswer && answerId === state.dateAnswer.answerId ? state.dateAnswer : {}),
          ...(extractDateFromString(dateString)),
          prev: dateString,
        },
      };
    }
    case STRINGS.QUESTION_TYPE.COMPOUND: {
      const previousAnswersWithTags = _reduce(previousAnswers, (result, answer) => {
        const correspondingQuestion = _find(question.questions, subQuestion => _find(subQuestion.answers, { answerId: answer.answerId }));
        const compoundQuestion = _find(question.questions, question => question.type === COMPOUND);
        if (compoundQuestion?.type === COMPOUND && !correspondingQuestion) {
          const correspondingSubQuestion = _find(compoundQuestion.questions, subQuestion => _find(subQuestion.answers, { answerId: answer.answerId }));
          return { ...result, [correspondingSubQuestion.questionId]: { ...answer, questionTags: correspondingSubQuestion?.tags, value: answer.value || '' } };
        }
        if (correspondingQuestion?.type === 'DATE') {
          return { ...result, [correspondingQuestion.questionId]: { ...answer, value: extractDateFromString(answer.valueDate), questionTags: correspondingQuestion.tags } };
        }
        if (correspondingQuestion?.type === 'SINGLE') {
          return { ...result, [correspondingQuestion.questionId]: { ...answer, value: answer, questionTags: correspondingQuestion?.tags } };
        }
        return { ...result, [correspondingQuestion.questionId]: { ...answer, questionTags: correspondingQuestion?.tags, value: answer.value || '' } };
      }, {});
      return { compoundAnswers: previousAnswersWithTags };
    }
    case STRINGS.QUESTION_TYPE.FREETEXT:
      return { freeTextAnswer: _get(previousAnswers[0], 'value', '') };
    case STRINGS.QUESTION_TYPE.BLOODPRESSURE: {
      const bPFromResponse = _get(previousAnswers[0], 'jsonValue.bloodPressure', '');
      const bPWithTimeSeparated = splitDateAndTime(bPFromResponse);
      const bloodPressure = addRemovableFlag(bPWithTimeSeparated);
      return {
        bloodPressureAnswer: {
          bloodPressure: toObject(bloodPressure),
        },
      };
    }
    case STRINGS.QUESTION_TYPE.BODYMASSINDEX: {
      const bmiInResponse = _get(previousAnswers[0], 'jsonValue', '');
      const isWeightImperial = !bmiInResponse.weightKilograms;
      const isHeightImperial = !bmiInResponse.heightCentimetres;
      return {
        bmi: {
          ...initialState.bmi,
          isWeightImperial,
          isHeightImperial,
          weight: {
            stone: bmiInResponse.weightStone || '',
            pounds: bmiInResponse.weightPounds || '',
            kilograms: bmiInResponse.weightKilograms || '',
          },
          height: {
            feet: bmiInResponse.heightFeet || '',
            inches: bmiInResponse.heightInches || '',
            centimetres: bmiInResponse.heightCentimetres || '',
          },
        },
      };
    }
    case STRINGS.QUESTION_TYPE.PHOTO: {
      if (previousAnswers[0].jsonValue) {
        return {
          photos: {
            ...state.photos,
            [question.questionId]: _mapValues(state.photos[question.questionId], photo => (
              { ...(_find(previousAnswers[0].jsonValue.photos, photoFromAPI => photoFromAPI.fileName === photo.fileName)), ...photo }
            )),
          },
        };
      }
      return {};
    }
    case STRINGS.QUESTION_TYPE.LOGIN: {
      if (previousAnswers[0].jsonValue) {
        return { loginDetails: previousAnswers[0].jsonValue };
      }
      return {};
    }
    case STRINGS.QUESTION_TYPE.SEARCH: {
      const previousAnswer = previousAnswers[0].jsonValue;
      const searchTerm = _size(previousAnswer) > 1
        ? getSearchResultDisplayName(previousAnswer, question.searchDisplayFields)
        : previousAnswer?.name;
      return {
        selectedSearchResult: previousAnswer,
        searchTerm: searchTerm || question.searchNotFoundButtonText || 'Not listed',
      };
    }
    default:
      return { freeTextAnswer: _get(previousAnswers[0], 'value', '') };
  }
};

export const consultationReducer = (preloadedState = {}, action = {}) => {
  const state = { ...initialState, ...preloadedState };
  switch (action.type) {
    case consultation.GET_API_VERSION_SUCCESS:
      return { ...state, error: initialState.error, apiVersion: action.payload.build.version };
    case consultation.GET_API_VERSION_ERROR:
      return { ...state, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.GET_TREATMENT_CATEGORY_SUCCESS:
      return {
        ...state,
        error: initialState.error,
        treatmentCategory: action.payload.codeName,
        treatmentCategoryDetails: {
          ...state.treatmentCategoryDetails,
          name: action.payload.name,
          version: action.payload.version,
        },
      };
    case consultation.GET_TREATMENT_CATEGORY_ERROR:
      return { ...state, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.SET_DISCLAIMER_QUESTION:
      return {
        ...state,
        responseType: STRINGS.RESPONSE_TYPE.QUESTION,
        next: true,
        isDisclaimerQuestion: true,
        question: action.payload,
      };
    case consultation.GET_FIRST_QUESTION_REQUEST:
      return { ...state, isLoading: true, isInternalRedirectBlockResume: false, error: initialState.error };
    case consultation.GET_FIRST_QUESTION_ERROR:
      return { ...state, isLoading: false, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.GET_FIRST_QUESTION_SUCCESS:
      return {
        ...state,
        isDisclaimerQuestion: false,
        validationErrors: [],
        multiSelectedAnswers: initialState.multiSelectedAnswers,
        publicHealthAlertHtml: initialState.publicHealthAlertHtml,
        isLoading: false,
        syncRequest: initialState.syncRequest,
        questionIndex: state.questionIndex + 1,
        compoundAnswers: action.payload.response.question.type === COMPOUND ? primeCompoundAnswer(action.payload.response.question.questions) : initialState.compoundAnswers,
        ...action.payload.response,
      };
    case consultation.GET_FIRST_PRELIM_QUESTION_REQUEST:
      return { ...state, isLoading: true, isInternalRedirectBlockResume: false, error: initialState.error };
    case consultation.GET_FIRST_PRELIM_QUESTION_ERROR:
      return { ...state, isLoading: false, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.GET_FIRST_PRELIM_QUESTION_SUCCESS:
      return {
        ...state,
        isDisclaimerQuestion: false,
        validationErrors: [],
        multiSelectedAnswers: initialState.multiSelectedAnswers,
        publicHealthAlertHtml: initialState.publicHealthAlertHtml,
        isLoading: false,
        syncRequest: initialState.syncRequest,
        questionIndex: state.questionIndex + 1,
        compoundAnswers: action.payload.response.question.type === COMPOUND ? primeCompoundAnswer(action.payload.response.question.questions) : initialState.compoundAnswers,
        ...action.payload.response,
      };
    case consultation.GET_NEXT_QUESTION_REQUEST:
      return { ...state,
        error: initialState.error,
        isLoading: true,
        previous: true,
        isForwardNavigation: true,
        isInternalRedirectBlockResume: false,
        interstitialText: state.question.interstitialText };
    case consultation.GET_NEXT_QUESTION_SUCCESS:
      return (
        {
          ...state,
          validationErrors: [],
          inputsReadOnly: initialState.inputsReadOnly,
          previousAnswers: initialState.previousAnswers,
          ...action.payload.response,
          isLoading: false,
          syncRequest: initialState.syncRequest,
          singleSelectedAnswer: initialState.singleSelectedAnswer,
          multiSelectedAnswers: initialState.multiSelectedAnswers,
          dateAnswer: initialState.dateAnswer,
          freeTextAnswer: initialState.freeTextAnswer,
          compoundAnswers: action.payload.response.question.type === COMPOUND ? primeCompoundAnswer(action.payload.response.question.questions) : initialState.compoundAnswers,
          addressesAPIResults: initialState.addressesAPIResults,
          postcodeAPIResults: initialState.postcodeAPIResults,
          bloodPressureAnswer: initialState.bloodPressureAnswer,
          loginDetails: initialState.loginDetails,
          bmi: initialState.bmi,
          searchTerm: initialState.searchTerm,
          searchResults: initialState.searchResults,
          selectedSearchResult: initialState.selectedSearchResult,
          interstitialText: initialState.interstitialText,
          questionIndex: state.questionIndex + 1,
          ...(prefillDemographicData(action.payload)),
          ...(updateAnswersWithPrevious(action.payload.response, state)),
        }
      );
    case consultation.GET_NEXT_QUESTION_ERROR:
      return { ...state, isLoading: false, apiError: action.payload, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.GET_NEXT_QUESTION_COMPOUND_QUESTION_ERROR:
      return { ...state, isLoading: false, validationErrors: [action.payload] };
    case consultation.GET_NEXT_QUESTION_INVALID_REQUEST_ERROR:
      return { ...state, isLoading: false, validationErrors: [{ localError: action.payload, message: action.payload }] };
    case consultation.SYNC_TO_API_CURRENT_QUESTION:
      return { ...state,
        syncRequest: true,
        question: { questionId: action.payload, text: '', type: STRINGS.QUESTION_TYPE.SINGLE },
        previousAnswers: initialState.previousAnswers,
        singleSelectedAnswer: initialState.singleSelectedAnswer };
    case consultation.RETURN_TO_WEBFLOW:
      return { ...state,
        question: { questionId: action.payload, text: '', type: STRINGS.QUESTION_TYPE.SINGLE },
        previousAnswers: initialState.previousAnswers,
        singleSelectedAnswer: initialState.singleSelectedAnswer,
        multiSelectedAnswers: initialState.multiSelectedAnswers,
        dateAnswer: initialState.dateAnswer,
        freeTextAnswer: initialState.freeTextAnswer,
        interstitialText: initialState.interstitialText,
        searchResults: initialState.searchResults,
        selectedSearchResult: initialState.selectedSearchResult };
    case consultation.OUT_OF_HOURS_ERROR:
      return { ...state, isLoading: false, apiError: action.payload, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.SELECT_SINGLE_ANSWER:
      return { ...state, singleSelectedAnswer: action.payload };
    case consultation.SELECT_MULTIPLE_ANSWERS:
      return { ...state, multiSelectedAnswers: [...state.multiSelectedAnswers, action.payload] };
    case consultation.DESELECT_MULTIPLE_ANSWERS:
      return { ...state, multiSelectedAnswers: state.multiSelectedAnswers.filter(item => item.answerId !== action.payload.answerId) };
    case consultation.GET_PREVIOUS_QUESTION_REQUEST:
      return { ...state, error: initialState.error, isLoading: true, isForwardNavigation: false };
    case consultation.GET_PREVIOUS_QUESTION_SUCCESS:
      return { ...state,
        inputsReadOnly: initialState.inputsReadOnly,
        singleSelectedAnswer: initialState.singleSelectedAnswer,
        multiSelectedAnswers: initialState.multiSelectedAnswers,
        dateAnswer: initialState.dateAnswer,
        freeTextAnswer: initialState.freeTextAnswer,
        compoundAnswers: initialState.compoundAnswers,
        addressesAPIResults: initialState.addressesAPIResults,
        postcodeAPIResults: initialState.postcodeAPIResults,
        bloodPressureAnswer: initialState.bloodPressureAnswer,
        loginDetails: initialState.loginDetails,
        bmi: initialState.bmi,
        previousAnswers: initialState.previousAnswers,
        searchTerm: initialState.searchTerm,
        searchResults: initialState.searchResults,
        selectedSearchResult: initialState.selectedSearchResult,
        ...action.payload.response,
        validationErrors: [],
        questionIndex: state.questionIndex - 1,
        ...(prefillDemographicData(action.payload)),
        ...(updateAnswersWithPrevious(action.payload.response, state)),
        syncRequest: initialState.syncRequest,
        isLoading: false };
    case consultation.GET_PREVIOUS_QUESTION_ERROR:
      return { ...state, isLoading: false, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.UPDATE_DATE_DAY:
      return { ...state, dateAnswer: { ...state.dateAnswer, d: action.payload } };
    case consultation.UPDATE_DATE_MONTH:
      return { ...state, dateAnswer: { ...state.dateAnswer, m: action.payload } };
    case consultation.UPDATE_DATE_YEAR:
      return { ...state, dateAnswer: { ...state.dateAnswer, y: action.payload } };
    case consultation.UPDATE_FREETEXT_ANSWER:
      return { ...state, freeTextAnswer: action.payload };
    case consultation.SUBMIT_CONSULTATION_SUCCESS:
      return { ...state, submissionDetails: action.payload, photos: {} };
    case consultation.SUBMIT_CONSULTATION_ERROR:
      return { ...state, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload, isLoading: false, photos: {} };
    case consultation.ADD_ALLMULTIPLE_VALIDATION:
      return { ...state, validationErrors: [{ message: action.payload }] };
    case consultation.SET_DATE_ANSWER_ID:
      return { ...state, dateAnswer: { ...state.dateAnswer, answerId: action.payload } };
    case consultation.UPDATE_COMPOUND_ANSWERS:
      return { ...state, compoundAnswers: { ...state.compoundAnswers, ...action.payload } };
    case consultation.SET_START_TIME:
      return { ...state, consultationStartTime: action.payload };
    case consultation.SET_PAUSED:
      return { ...state, paused: action.payload };
    case consultation.SET_PRACTICE:
      return { ...state, practice: action.payload };
    case consultation.SET_ENCOUNTER_ID:
      return { ...state, encounterId: action.payload };
    case consultation.DUPLICATE_SUBMISSION_ERROR:
      return { ...state, isDuplicateSubmission: true, isLoading: false };
    case consultation.CLOSE_DUPLICATE_SUBMISSION_MODAL:
      return { ...state, isDuplicateSubmission: false, isAttemptingToLeavePage: false };
    case consultation.SET_FEDERATED_LOGIN_PROVIDER:
      return { ...state, federatedLoginProvider: action.payload };
    case consultation.VALIDATE_POSTCODE_SUCCESS:
      return { ...state, postcodeAPIResults: action.payload, postcodeValidated: _size(action.payload) === 1, isLoading: false };
    case consultation.VALIDATE_POSTCODE_ERROR:
      return { ...state, postcodeAPIResults: [], responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload, postcodeValidated: false, isLoading: false };
    case consultation.FIND_ADDRESSES_SUCCESS:
      return { ...state, addressesAPIResults: action.payload, isLoading: false };
    case consultation.FIND_ADDRESSES_ERROR:
      return { ...state, addressesAPIResults: [',,,'], isLoading: false };
    case consultation.CLEAR_ADDRESSES:
      return { ...state, addressesAPIResults: [] };
    case consultation.UPDATE_BP_READING: {
      const { id, field, value } = action.payload;
      const { bloodPressure } = state.bloodPressureAnswer;
      return {
        ...state,
        bloodPressureAnswer: {
          ...state.bloodPressureAnswer,
          bloodPressure: {
            ...bloodPressure,
            [id]: { ...bloodPressure[id], [field]: value },
          },
        },
      };
    }
    case consultation.ADD_BP_READING: {
      const { bloodPressure } = state.bloodPressureAnswer;
      return {
        ...state,
        bloodPressureAnswer: {
          ...state.bloodPressureAnswer,
          bloodPressure: {
            ...bloodPressure,
            ...action.payload,
          },
        },
      };
    }
    case consultation.REMOVE_BP_READING: {
      const { bloodPressure } = state.bloodPressureAnswer;
      return {
        ...state,
        bloodPressureAnswer: {
          ...state.bloodPressureAnswer,
          bloodPressure: _omitBy(bloodPressure, (reading, key) => key === action.payload),
        },
      };
    }
    case consultation.UPDATE_BMI_WEIGHT:
      return { ...state, bmi: { ...state.bmi, weight: { ...state.bmi.weight, ...action.payload } } };
    case consultation.UPDATE_BMI_HEIGHT:
      return { ...state, bmi: { ...state.bmi, height: { ...state.bmi.height, ...action.payload } } };
    case consultation.TOGGLE_BMI_WEIGHT:
      return {
        ...state,
        bmi: {
          ...state.bmi,
          isWeightImperial: !state.bmi.isWeightImperial,
          weight: { ...initialState.bmi.weight, ...action.payload },
        },
      };
    case consultation.TOGGLE_BMI_HEIGHT:
      return {
        ...state,
        bmi: {
          ...state.bmi,
          isHeightImperial: !state.bmi.isHeightImperial,
          height: { ...initialState.bmi.height, ...action.payload },
        },
      };
    case consultation.UPLOAD_PHOTO_TO_S3_REQUEST: {
      const { photoId, ...placeholderPhoto } = action.payload;
      return {
        ...state,
        validationErrors: initialState.validationErrors,
        photos: {
          ...state.photos,
          [state.question.questionId]: { ...state.photos[state.question.questionId], [photoId]: placeholderPhoto },
        },
      };
    }
    case consultation.UPLOAD_PHOTO_TO_S3_SUCCESS:
    case consultation.ADD_PHOTO: {
      const { photoId, ...photoData } = action.payload;
      return {
        ...state,
        validationErrors: initialState.validationErrors,
        photos: {
          ...state.photos,
          [state.question.questionId]: {
            ...state.photos[state.question.questionId],
            [photoId]: {
              ...photoData,
              // keep description if one has been typed in by the user during photo attachment
              fileDescription: state.photos[state.question.questionId]?.[photoId]?.fileDescription || '',
            },
          },
        },
      };
    }
    case consultation.UPLOAD_PHOTO_TO_S3_ERROR: {
      const { photoId, errorMessage } = action.payload;
      return {
        ...state,
        validationErrors: [{ localError: errorMessage }],
        photos: {
          ...state.photos,
          [state.question.questionId]: _omitBy(state.photos[state.question.questionId], (photo, key) => key === photoId),
        },
      };
    }
    case consultation.DELETE_PHOTO: {
      return {
        ...state,
        photos: {
          ...state.photos,
          [state.question.questionId]: _omitBy(state.photos[state.question.questionId], (photo, key) => key === action.payload),
        },
      };
    }
    case consultation.DELETE_PHOTO_FROM_S3_REQUEST: {
      return {
        ...state,
        photos: {
          ...state.photos,
          [state.question.questionId]: {
            ...state.photos[state.question.questionId],
            [action.payload]: { ...state.photos[state.question.questionId][action.payload], optimisticDelete: true },
          },
        },
      };
    }
    case consultation.DELETE_PHOTO_FROM_S3_SUCCESS: {
      return {
        ...state,
        photos: {
          ...state.photos,
          [state.question.questionId]: _omitBy(state.photos[state.question.questionId], (photo, key) => key === action.payload),
        },
      };
    }
    case consultation.DELETE_PHOTO_FROM_S3_ERROR: {
      const { photoId, errorMessage } = action.payload;
      return {
        ...state,
        validationErrors: [{ localError: errorMessage }],
        photos: {
          ...state.photos,
          [state.question.questionId]: {
            ...state.photos,
            [state.question.questionId]: {
              ...state.photos[state.question.questionId],
              [photoId]: { ...state.photos[state.question.questionId][photoId], optimisticDelete: undefined },
            },
          },
        },
      };
    }
    case consultation.UPDATE_PHOTO_DESCRIPTION:
      return {
        ...state,
        photos: {
          ...state.photos,
          [state.question.questionId]: {
            ...state.photos[state.question.questionId],
            [action.payload.id]: {
              ...state.photos[state.question.questionId][action.payload.id],
              fileDescription: action.payload.fileDescription,
            },
          },
        },
      };
    case consultation.GET_LAST_QUESTION_REQUEST:
      return { ...state, isLoading: true, isLoadingLastQuestion: true, isForwardNavigation: true, isInternalRedirectBlockResume: false };
    case consultation.GET_LAST_QUESTION_SUCCESS:
      return (
        {
          ...state,
          inputsReadOnly: initialState.inputsReadOnly,
          validationErrors: [],
          previousAnswers: initialState.previousAnswers,
          ...action.payload.response,
          isLoading: false,
          syncRequest: initialState.syncRequest,
          isLoadingLastQuestion: false,
          singleSelectedAnswer: initialState.singleSelectedAnswer,
          multiSelectedAnswers: initialState.multiSelectedAnswers,
          dateAnswer: initialState.dateAnswer,
          freeTextAnswer: initialState.freeTextAnswer,
          compoundAnswers: action.payload.response.question.type === COMPOUND ? primeCompoundAnswer(action.payload.response.question.questions) : initialState.compoundAnswers,
          addressesAPIResults: initialState.addressesAPIResults,
          postcodeAPIResults: initialState.postcodeAPIResults,
          loginDetails: initialState.loginDetails,
          searchTerm: initialState.searchTerm,
          searchResults: initialState.searchResults,
          selectedSearchResult: initialState.selectedSearchResult,
          ...(prefillDemographicData(action.payload)),
          ...(updateAnswersWithPrevious(action.payload.response, state)),
        }
      );
    case consultation.GET_LAST_QUESTION_ERROR:
      return { ...state, isLoading: false, isLoadingLastQuestion: false, apiError: action.payload, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload };
    case consultation.GET_PUBLIC_HEALTH_ALERT_SUCCESS:
      return { ...state, publicHealthAlertHtml: action.payload };
    case consultation.INTERNAL_REDIRECT_REQUEST:
      return { ...state, isInternalRedirectBlockResume: true };
    case consultation.INTERNAL_REDIRECT_SUCCESS:
      return { ...state, isInternalRedirectBlockResume: false };
    case consultation.SAVE_IS_LOGGED_IN:
      return { ...state, isLoggedIn: true };
    case consultation.SET_IS_CONTINUE_AS_GUEST:
      return { ...state, isContinueAsGuest: true };
    case consultation.SET_PRODUCT:
      return { ...state, product: action.payload };
    case consultation.SET_PATIENT:
      return { ...state, patient: action.payload };
    case consultation.SET_TWO_WAY_MESSAGE_ID:
      return { ...state, twoWayMessageId: action.payload };
    case consultation.UPDATE_SEARCH_TERM:
      return { ...state, searchTerm: action.payload };
    case consultation.GET_TYPE_AHEAD_SEARCH_SUCCESS:
      return { ...state, searchResults: { ...state.searchResults, ...action.payload } };
    case consultation.SELECT_SEARCH_RESULT:
      return { ...state, selectedSearchResult: action.payload.result, searchTerm: action.payload.displayName };
    case STRINGS.SESSION_REDUCER.CLOSE_SESSION_EXPIRY_WARNING:
      return { ...state, isSessionExpiryWarningOpen: false };
    case STRINGS.SESSION_REDUCER.CLOSE_DEMO_NOTICE:
      return { ...state, isDemoNoticeOpen: false };
    case STRINGS.SESSION_REDUCER.CLOSE_LEAVE_PAGE_WARNING:
      return { ...state, isAttemptingToLeavePage: false };
    case STRINGS.SESSION_REDUCER.OPEN_LEAVE_PAGE_WARNING:
      return { ...state, isAttemptingToLeavePage: true };
    case STRINGS.SESSION_REDUCER.OPEN_SESSION_EXPIRY_WARNING:
      return { ...state, isSessionExpiryWarningOpen: true };
    case STRINGS.SESSION_REDUCER.RESET_SESSION_TIMEOUT:
      return { ...state, sessionExpiryTime: action.payload };
    case STRINGS.SESSION_REDUCER.CLEAR_SESSION_TIMEOUT:
      return { ...state, sessionExpiryTime: {} };
    case STRINGS.SESSION_REDUCER.GA_INITIALISED:
      return { ...state, isGAInitialised: action.payload };
    case STRINGS.SESSION_REDUCER.EXTEND_SESSION_ERROR:
      return { ...state, responseType: STRINGS.RESPONSE_TYPE.ERROR, error: action.payload, isLoading: false };
    case STRINGS.SESSION_REDUCER.RESTART_SESSION:
      return { ...initialState };
    default:
      return state;
  }
};
